<template>
	<v-expansion-panels v-model="panel" flat accordion>
		<div class="settings-description">
			The AI Assistant generates a text based on prompts given by the user. You can use this text in later steps.
		</div>
		<ActionCreditsRequiredBadge :action="creditsAction" utilClasses="mx-5 my-1" />
		<ExpansionPanel :title="'Settings'">
			<SettingSwitch :title="'Pre-Prompt'" :switchValue.sync="promptPrefixEnabled" />
			<div class="settings-description">
				Sent to the AI along with the first user prompt. Does not display in the interface. For example, “You are a writing assistant, come
				up with 5 blog post ideas for:”
			</div>
			<div class="align-center mb-4 prompt-prefix-row display-flex flex-row width-100" v-if="promptPrefixEnabled">
				<div class="width-100">
					<CombinedInputFieldDataMenu
						:value.sync="promptPrefix"
						:textAreaMode="false"
						:attachTo="'.prompt-prefix-row'"
						:valuesTypes="dataInputMenuValueTypes"
					/>
				</div>
			</div>

			<SettingSwitch
				:title="'Description'"
				:placeholder="'Type your description here'"
				:switchValue.sync="descriptionEnabled"
				:textFieldValue.sync="descriptionValue"
			/>
			<SettingSwitch :title="'Prefill'" :switchValue.sync="prefillEnabled" />
			<div class="align-center mb-4 prefill-row display-flex flex-row width-100" v-if="prefillEnabled">
				<div class="width-100">
					<CombinedInputFieldDataMenu
						:value.sync="prefillValue"
						:textAreaMode="false"
						:attachTo="'.prefill-row'"
						:valuesTypes="dataInputMenuValueTypes"
					/>
				</div>
			</div>
			<SettingSwitch :title="'Auto-send prefill'" :switchValue.sync="autoSendPrefilled" />

			<SettingSwitch :title="'Placeholder'" :switchValue.sync="placeholderEnabled" />
			<div class="align-center mb-4 placeholder display-flex flex-row width-100" v-if="placeholderEnabled">
				<div class="width-100">
					<CombinedInputFieldDataMenu
						:value.sync="placeholderValue"
						:textAreaMode="false"
						:attachTo="'.placeholder'"
						:valuesTypes="dataInputMenuValueTypes"
					/>
				</div>
			</div>
			<SettingSwitch title="Allow fine-tuning" :switchValue.sync="allowFineTuning" />
			<SettingSwitch title="Max words" placeholder="1400" :switchValue.sync="maxWordsEnabled" />
			<div class="settings-description">Up to 2500 words</div>
			<div class="align-center allow-fine-tuning display-flex flex-row width-100" v-if="maxWordsEnabled">
				<div class="width-100">
					<AppTextField outlined hide-details type="number" v-model="maxWordsValue" />
				</div>
			</div>
		</ExpansionPanel>
	</v-expansion-panels>
</template>

<script>
import ItemOnlyAllowedValueTypesMixin from './ValueTypesMixins/ItemOnlyAllowedValueTypesMixin';
import ExpansionPanel from './WidgetSettings/ExpansionPanel';
import SettingSwitch from './WidgetSettings/SettingSwitch';
import { AI_MINI_APP } from '@/components/BlockEditor/BlockEditorAppSelect/BlockEditorAppSelect.const';
import ActionCreditsRequiredBadge from '@/components/BlockEditor/IntegrationEditor/ActionCreditsRequiredBadge.vue';
import FieldSettingsMixin from '@/components/BlockEditorWidget/FieldSettings/FieldSettingsMixin';

export default {
	name: 'AIInputFieldSettings',
	mixins: [FieldSettingsMixin, ItemOnlyAllowedValueTypesMixin],
	components: {
		SettingSwitch,
		ExpansionPanel,
		ActionCreditsRequiredBadge,
	},
	computed: {
		allowFineTuning: {
			get() {
				return this.localField.metadata.options['allowFineTuning'].value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.allowFineTuning.value', value);
			},
		},
		maxWordsValue: {
			get() {
				return this.localField.metadata.options['maxWords'].value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.maxWords.value', value);
			},
		},
		maxWordsEnabled: {
			get() {
				return this.localField.metadata.options['maxWords'].enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.maxWords.enabled', value);
			},
		},
		promptPrefixEnabled: {
			get() {
				return this.localField.metadata.options['prompt_prefix'].enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prompt_prefix.enabled', value);
			},
		},
		promptPrefix: {
			get() {
				return this.localField.metadata.options['prompt_prefix'].value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prompt_prefix.value', value);
			},
		},
		autoSendPrefilled: {
			get() {
				return this.localField.metadata.options['auto_submit_prefilled'].enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.auto_submit_prefilled.enabled', value);
			},
		},
		promptSuffixEnabled: {
			get() {
				return this.localField.metadata.options['prompt_suffix'].enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prompt_suffix.enabled', value);
			},
		},
		promptSuffix: {
			get() {
				return this.localField.metadata.options['prompt_suffix'].value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.prompt_suffix.value', value);
			},
		},
		creditsAction() {
			return {
				...this.action,
				is_credit_based: !!this.action?.credits_configuration,
				credits_configuration: this.action?.credits_configuration[0][AI_MINI_APP],
			};
		},
	},
};
</script>

<style scoped>
.clr_box {
	background: #fcfcfd;
	width: 90px;
	height: 30px;
	border: 1px solid #c7cad5;
	border-radius: 5px;
	text-transform: uppercase;
	position: relative;
	display: inline-block;
	overflow: hidden;
}
.clr_num h5 {
	line-height: 30px;
	color: #8e949d;
}
.clr_right {
	width: 30px;
	height: 30px;
	position: absolute;
	right: 0px;
	top: 0px;
	border-left: 1px solid #c7cad5;
}
.clr_sel {
	height: 200px;
	overflow: hidden;
	box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.2);
}

.settings-description {
	margin: 0 0 4px 0;
	text-align: left;
	font-weight: 400;
	font-size: 12px;
	line-height: 14px;
}
</style>
